import { Typography, IconButton, Box, Tooltip } from "@mui/material";
import {
  AccessTime,
  Work,
  Build,
  Dashboard,
  BabyChangingStation,
  Edit,
  Delete,
  Add,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export const STATUS_COLORS = {
  Unconfirmed: "#ED9F59", // Orange
  Confirmed: "#3D60CF", // Blue
  Approved: "#29D55A", // Green
};

const EXCLUDED_PATTERNS = [/\(mil\)/i, /mil\)/i, /\(st\)/i];

const extractUnit = (str) => {
  // Only extract for the specific patterns
  if (/\(mil\)/i.test(str)) return "mil";
  if (/mil\)/i.test(str)) return "mil";
  if (/\(st\)/i.test(str)) return "st";
  return null;
};

const TimeReportItem = ({
  reportItem,
  onEdit,
  onDelete,
  onAdd,
  hideWorkOrderPrefix = false,
}) => {
  const { t } = useTranslation();

  const getIcon = (wageCode, type) => {
    if (type === "Workorder") return <Build />;
    if (type === "Project") return <Dashboard />;
    switch (true) {
      case wageCode === "F14":
      case wageCode === "F33":
        return <BabyChangingStation />;
      case wageCode?.startsWith("T"):
      case wageCode?.startsWith("A"):
        return <Work />;
      default:
        return <AccessTime />;
    }
  };

  const getText = (reportItem) => {
    if (reportItem.type === "Workorder") {
      if (hideWorkOrderPrefix) {
        return reportItem.name || reportItem.workOrderNumber;
      }
      return `${reportItem.workOrderNumber} - ${reportItem.name}`;
    } else if (reportItem.type === "Project") {
      return `${reportItem.projShortName} - ${reportItem.name}`;
    } else {
      return `${reportItem.wageCode} - ${reportItem.name}`;
    }
  };

  const formatDurationOrQuantity = (reportItem) => {
    // Handle negative durations correctly by using absolute values for hours/minutes
    // and prefixing a minus sign to the final string when needed.
    const isProject = reportItem.type === "Project";
    const targetStr = isProject
      ? reportItem.description || ""
      : reportItem.name || "";
    const match = EXCLUDED_PATTERNS.find((pattern) => pattern.test(targetStr));
    if (match) {
      // Show as quantity + unit, using only the specific patterns
      const unit = extractUnit(targetStr);
      const qty = reportItem.hours;
      return (
        <>
          {Number.isInteger(qty) ? qty : qty.toFixed(2)} {unit ? unit : ""}
        </>
      );
    }
    // Otherwise, show as duration
    const h = reportItem.hours;
    const isNegative = h < 0;
    const abs = Math.abs(h);
    let hours = Math.floor(abs);
    let minutes = Math.round((abs - hours) * 60);
    // Carry over to next hour if minutes round to 60
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }

    const text = t("timeReport.addTimeDialog.duration", {
      hours,
      minutes,
      defaultValue: `${hours} hours and ${minutes} minutes`,
    });
    return isNegative ? `- ${text}` : text;
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(reportItem);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(reportItem);
    }
  };

  const isEditable =
    reportItem.status !== "Approved" && reportItem.wageCode !== "X_100";

  const statusColor = STATUS_COLORS[reportItem.status] || "transparent";
  const statusTranslationKey = {
    Unconfirmed: "timeReport.reportedTimes.unconfirmed",
    Confirmed: "timeReport.reportedTimes.confirmed",
    Approved: "timeReport.reportedTimes.approved",
  }[reportItem.status];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        borderBottom: "1px solid #eee",
        "&:last-child": {
          borderBottom: "none",
        },
        backgroundColor:
          statusColor === "transparent" ? "inherit" : `${statusColor}0D`, // ~5% tint
        "&:not(:last-child)": {
          marginBottom: 1,
        },
        cursor: "default",
      }}
    >
      {/* Status accent bar */}
      <Tooltip
        title={
          statusTranslationKey ? t(statusTranslationKey) : reportItem.status
        }
        placement="left"
      >
        <Box
          aria-label={`Status: ${reportItem.status}`}
          sx={{
            width: 6,
            backgroundColor: statusColor,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
          }}
        />
      </Tooltip>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1,
          py: 2,
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
          {getIcon(reportItem.wageCode, reportItem.type)}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          {reportItem.type === "Project" ? (
            <>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {reportItem.projectActivityName || "-"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`${t("timeReport.addTimeDialog.subProject")}: ${
                  reportItem.subProjectName || "-"
                }`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {reportItem.reportCode}
                {reportItem.description ? ` - ${reportItem.description}` : ""}
              </Typography>
              <Typography variant="body2">
                {formatDurationOrQuantity(reportItem)}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {getText(reportItem)}
              </Typography>
              {reportItem.type === "Workorder" && (
                <Typography variant="body2" color="textSecondary" noWrap>
                  {reportItem.description !== ""
                    ? reportItem.description
                    : t("time")}
                </Typography>
              )}
              <Typography variant="body2">
                {formatDurationOrQuantity(reportItem)}
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
          {isEditable &&
            (reportItem.hours !== 0 ? (
              <>
                <IconButton
                  aria-label="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  size="small"
                >
                  <Edit />
                </IconButton>

                <IconButton
                  aria-label="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </>
            ) : (
              <IconButton
                aria-label="add"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onAdd) {
                    onAdd(reportItem);
                  }
                }}
                size="small"
              >
                <Add />
              </IconButton>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TimeReportItem;
